/**
 * El único punto por donde el panel habla con la API.
 *
 * Tres cosas que conviene que estén en un solo sitio y no repartidas por cada
 * pantalla: el token de acceso, el refresco cuando vence, y la traducción de los
 * errores RFC 7807 a algo que se pueda mostrar.
 */

/** Error de negocio de la API, ya legible. */
export class ErrorApi extends Error {
  constructor(
    readonly estado: number,
    /** El código del final del `type`: `mandato-duplicado`, `sin-permiso`… */
    readonly codigo: string,
    mensaje: string,
    /** Errores por campo, cuando la respuesta es de validación. */
    readonly campos: { campo: string; mensaje: string }[] = [],
    readonly traceId?: string,
  ) {
    super(mensaje);
    this.name = 'ErrorApi';
  }

  /** Para pintar el error justo debajo del campo que lo causó. */
  mensajeDe(campo: string): string | undefined {
    return this.campos.find((e) => e.campo === campo)?.mensaje;
  }
}

interface RespuestaProblema {
  type?: string;
  title?: string;
  detail?: string;
  traceId?: string;
  errores?: { campo: string; mensaje: string }[];
}

/**
 * El token de acceso vive en memoria, no en `localStorage`.
 *
 * Guardarlo en el almacenamiento del navegador lo deja al alcance de cualquier
 * script que llegue a la página. El refresco va en una cookie `HttpOnly` que el
 * JavaScript no puede leer, y es la que sobrevive a recargar la página.
 */
let tokenAcceso: string | null = null;
let alPerderSesion: (() => void) | null = null;

export function guardarToken(token: string | null): void {
  tokenAcceso = token;
}

export function hayToken(): boolean {
  return tokenAcceso !== null;
}

/** Lo llama el contexto de sesión para poder mandar al usuario a la entrada. */
export function alCerrarseLaSesion(accion: () => void): void {
  alPerderSesion = accion;
}

/** La cabecera que `/auth/refresh` exige presente; es su defensa contra CSRF. */
const CABECERA_REFRESCO = 'X-Refresh';

let refrescoEnCurso: Promise<boolean> | null = null;

/**
 * Pide un token nuevo con la cookie de refresco.
 *
 * Se comparte la promesa entre llamadas simultáneas: al abrir una pantalla con
 * cuatro consultas a la vez, todas reciben el 401 casi al tiempo, y cuatro
 * refrescos en paralelo harían que tres de ellos parecieran un token reusado —
 * que es justo lo que la API interpreta como sesión comprometida y revoca.
 */
async function refrescar(): Promise<boolean> {
  if (!refrescoEnCurso) {
    refrescoEnCurso = (async () => {
      try {
        const respuesta = await fetch('/api/v1/auth/refresh', {
          method: 'POST',
          headers: { [CABECERA_REFRESCO]: '1' },
          credentials: 'include',
        });
        // 204 = no hay cookie, asi que tampoco hay sesion que rescatar.
        if (respuesta.status === 204 || !respuesta.ok) return false;
        const datos = (await respuesta.json()) as { accessToken: string };
        tokenAcceso = datos.accessToken;
        return true;
      } catch {
        return false;
      } finally {
        // Se libera en el siguiente tick para que quien llegó tarde a esta
        // misma promesa la reciba resuelta y no arranque otro refresco.
        setTimeout(() => {
          refrescoEnCurso = null;
        }, 0);
      }
    })();
  }
  return refrescoEnCurso;
}

interface Opciones {
  metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  cuerpo?: unknown;
  /** Para subir archivos: el navegador pone el `Content-Type` con su frontera. */
  formulario?: FormData;
  /** Uso interno: evita reintentar en bucle tras un refresco fallido. */
  reintento?: boolean;
}

export async function api<T>(ruta: string, opciones: Opciones = {}): Promise<T> {
  const cabeceras: Record<string, string> = {};
  if (tokenAcceso) cabeceras.Authorization = `Bearer ${tokenAcceso}`;
  if (opciones.cuerpo !== undefined) cabeceras['Content-Type'] = 'application/json';

  const respuesta = await fetch(`/api/v1${ruta}`, {
    method: opciones.metodo ?? 'GET',
    headers: cabeceras,
    body: opciones.formulario ?? (opciones.cuerpo !== undefined ? JSON.stringify(opciones.cuerpo) : undefined),
    credentials: 'include',
  });

  // Un 401 de las rutas de autenticación no significa «tu token caducó», sino
  // «esas credenciales no valen». Refrescar ahí gastaba un intento con la
  // cookie de otra sesión y, sobre todo, sustituía el mensaje de la API por
  // «La sesión terminó» a alguien que nunca llegó a tener una.
  const esAutenticacion = ruta.startsWith('/auth/');

  if (respuesta.status === 401 && !opciones.reintento && !esAutenticacion) {
    if (await refrescar()) {
      return api<T>(ruta, { ...opciones, reintento: true });
    }
    tokenAcceso = null;
    alPerderSesion?.();
    throw new ErrorApi(401, 'no-autenticado', 'La sesión terminó. Vuelve a entrar.');
  }

  if (respuesta.status === 204) return undefined as T;

  const texto = await respuesta.text();
  const datos = texto ? (JSON.parse(texto) as unknown) : null;

  if (!respuesta.ok) {
    const problema = (datos ?? {}) as RespuestaProblema;
    throw new ErrorApi(
      respuesta.status,
      problema.type?.split('/').pop() ?? 'error',
      problema.detail ?? problema.title ?? 'Algo salió mal.',
      problema.errores ?? [],
      problema.traceId,
    );
  }
  return datos as T;
}

/** Página de Spring Data, tal como la devuelve la API. */
export interface Pagina<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

/** Arma la cadena de consulta descartando lo vacío, que es la mitad de los filtros. */
export function consulta(parametros: Record<string, unknown>): string {
  const partes = new URLSearchParams();
  for (const [clave, valor] of Object.entries(parametros)) {
    if (valor === undefined || valor === null || valor === '') continue;
    if (Array.isArray(valor)) {
      valor.filter((v) => v !== '' && v != null).forEach((v) => partes.append(clave, String(v)));
    } else {
      partes.set(clave, String(valor));
    }
  }
  const texto = partes.toString();
  return texto ? `?${texto}` : '';
}
